import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { Form } from "@/components/Form";
import { Input } from "@/components/Input";
import { HeaderTextBlock } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import {
  useConsentFormFields,
  usePostPrivacyPolicyUrl,
} from "@/requests/hooks/collect";
import {
  NAVIGATE_FORWARDS,
  SUPPORT_MEETING_URL,
  URL_REGEX,
} from "@/utils/const";
import formatError from "@/utils/formatError";
import { getOnboardingUrl } from "@/utils/onboardingManager";
import { faFlag } from "@fortawesome/pro-duotone-svg-icons";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InferType, object, string } from "yup";

let hasFormBeenSubmitted = false;

const formSchema = object({
  url: string()
    .matches(URL_REGEX, {
      message: t("emailOnboarding.privacyPolicyUrl.right.input.invalid"),
    })
    .required(t("emailOnboarding.privacyPolicyUrl.right.input.required")),
});

type IPrivacyPolicyUrl = InferType<typeof formSchema>;

const PrivacyPolicyURL = () => {
  const navigate = useNavigate();

  const { data } = useConsentFormFields();
  const privacyPolicyUrl = data?.privacyPolicyUrl;
  const {
    mutate: sendPrivacyPolicyUrl,
    error,
    isPending,
    isSuccess,
  } = usePostPrivacyPolicyUrl();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    setError,
    setValue,
  } = useForm<IPrivacyPolicyUrl>({
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    if (!!privacyPolicyUrl) {
      setValue("url", privacyPolicyUrl);
    }
  }, [privacyPolicyUrl]);

  useEffect(() => {
    if (hasFormBeenSubmitted) trigger();
  }, [watch("url")]);

  useEffect(() => {
    if (isSuccess) navigateToNextScreen();
  }, [isSuccess]);

  const navigateToNextScreen = () => {
    navigate(getOnboardingUrl("PRIVACY_POLICY_URL_SAVED"), {
      state: {
        direction: NAVIGATE_FORWARDS,
      },
    });
  };

  useEffect(() => {
    if (!error) return;

    const { codes } = formatError(error);

    if (codes?.includes("INVALID_PRIVACY_POLICY")) {
      setError("url", {
        message: t("emailOnboarding.privacyPolicyUrl.right.input.invalid"),
      });
    }
  }, [error]);

  const onClickSendPrivacyPolicyUrl = ({ url }: IPrivacyPolicyUrl) => {
    if (!hasFormBeenSubmitted) hasFormBeenSubmitted = true;

    if (!!privacyPolicyUrl && privacyPolicyUrl === url) {
      navigateToNextScreen();
      return;
    }

    if (!url.includes("https://") && !url.includes("http://")) {
      url = `https://${url}`;
    }

    sendPrivacyPolicyUrl({
      privacyPolicyUrl: url,
      version: data?.version,
    });
  };

  const renderDescriptionText = () => (
    <div className="mt-10">
      <Trans i18nKey="emailOnboarding.privacyPolicyUrl.left.header.description">
        one
        <span className="font-semibold text-secondary">two</span>
        three
        <span className="font-semibold text-secondary">four</span>
        five
      </Trans>
    </div>
  );

  const renderInput = () => (
    <Form
      id="privacy-policy-url-form"
      onSubmit={handleSubmit(onClickSendPrivacyPolicyUrl)}>
      <Input
        {...register("url")}
        label={t("emailOnboarding.privacyPolicyUrl.right.input.label")}
        placeholder={t("emailOnboarding.privacyPolicyUrl.right.input.label")}
        helperText={errors?.url?.message}
        variant={errors?.url?.message ? "error" : undefined}
        testId="privacy-policy-url-input"
        helperTextTestId="privacy-policy-url-helper-text"
      />
    </Form>
  );

  const renderSubmitCTA = () => (
    <Button
      variant="primary"
      trackingId={{
        eventName: "onboarding.collect.consent-form.privacy-policy-url.submit",
      }}
      type="submit"
      form="privacy-policy-url-form"
      loading={isPending}
      disabled={isPending || !!errors?.url?.message}
      testId="privacy-policy-url-cta">
      {t("emailOnboarding.privacyPolicyUrl.right.submit")}
    </Button>
  );

  const renderRightPanel = () => (
    <div className="mt-20 flex flex-col items-start gap-20 md:mt-30 md:flex-row">
      <div className="w-full flex-grow md:w-[unset]">{renderInput()}</div>
      <div className="w-full flex-shrink-0 pt-[unset] md:w-[unset] md:pt-5">
        {renderSubmitCTA()}
      </div>
    </div>
  );

  const renderLeftPanel = () => (
    <HeaderTextBlock
      align="left"
      featureText={t("emailOnboarding.privacyPolicyUrl.left.header.feature")}
      featureTextColor="primary"
      headingText={t("emailOnboarding.privacyPolicyUrl.left.header.heading")}
      headingTextColor="secondary"
      customHtml={renderDescriptionText()}
      customHtmlPosition="bottom"
      descriptionTextColor="secondary"
    />
  );

  const renderCenterPanel = () => (
    <div className="w-full">
      <Alert
        icon={faFlag}
        titleSlot={
          <span className="text-h5 font-semibold">
            {t("emailOnboarding.privacyPolicyUrl.center.title")}
          </span>
        }>
        <div className="mb-20">
          {t("emailOnboarding.privacyPolicyUrl.center.description_1")}
        </div>

        <Trans i18nKey="emailOnboarding.privacyPolicyUrl.center.description_2">
          zero
          <Button
            as="a"
            variant="link"
            href={SUPPORT_MEETING_URL}
            width="unset"
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="h-15">
            one
          </Button>
          two
        </Trans>
      </Alert>
    </div>
  );

  return (
    <SplitLayout
      leftPanel={renderLeftPanel()}
      rightPanel={renderRightPanel()}
      centerPanel={renderCenterPanel()}
      backButtonPath={getOnboardingUrl("KLAVIYO_ENABLED")}
    />
  );
};

export default PrivacyPolicyURL;
